import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Footer from "../Footer/Footer";
import NavBar from "../NavBar/NavBar";
import { classes } from "../../Data";
import "./Classes.css";
import { useFetchAllContentQuery } from "../../Redux/Api/AuthApi";
import { Helmet } from "react-helmet-async";

const Classes = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProgram, setSelectedProgram] = useState("all");
  const [selectedAge, setSelectedAge] = useState("all");
  const [selectedDay, setSelectedDay] = useState("all");
  const [filteredClasses, setFilteredClasses] = useState(classes);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [contentData, setContentData] = useState(null);

  // Extract unique programs, age groups, and days from data
  const programs = [
    "all",
    ...new Set(classes.map((c) => c.program).filter(Boolean)),
  ];
  const ageGroups = [
    "all",
    ...new Set(classes.map((c) => c.ageGroup).filter(Boolean)),
  ];
  const days = [
    "all",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",   // Change to "smooth" if you prefer
    });
  }, []);
  useEffect(() => {
    let filtered = classes;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(
        (classItem) =>
          classItem.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          classItem.description
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase())
      );
    }

    // Filter by program
    if (selectedProgram !== "all") {
      filtered = filtered.filter(
        (classItem) => classItem.program === selectedProgram
      );
    }

    // Filter by age group
    if (selectedAge !== "all") {
      filtered = filtered.filter(
        (classItem) => classItem.ageGroup === selectedAge
      );
    }

    // Filter by day
    if (selectedDay !== "all") {
      filtered = filtered.filter((classItem) =>
        classItem.day?.includes(selectedDay)
      );
    }

    setFilteredClasses(filtered);
  }, [searchTerm, selectedProgram, selectedAge, selectedDay]);

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedProgram("all");
    setSelectedAge("all");
    setSelectedDay("all");
  };

  const getAgeBadgeColor = (ageGroup) => {
    if (ageGroup?.includes("1.5")) return "#10b981";
    if (ageGroup?.includes("3-5")) return "#3b82f6";
    if (ageGroup?.includes("5-16")) return "#8b5cf6";
    if (ageGroup?.includes("5+")) return "#f59e0b";
    return "#ef4444";
  };

  const { data, isLoading, isSuccess } = useFetchAllContentQuery();

  useEffect(() => {
    if (isSuccess) {
      setContentData(data);
    }
  }, [data, isSuccess]);

  return (
    <>
    <Helmet>
      <title>Classes</title>
      <meta
       name="description"
       content="Discover the perfect gymnastics program for your child 
        "/>
     <link rel="canonical" href="/"/>
    </Helmet>
    <div className="kiga-classes-main">
      <NavBar />

      {/* Hero Section */}
      <section className="kiga-classes-hero">
        <div className="kiga-classes-hero-content">
          <h1>Our Classes</h1>
          <p>Discover the perfect gymnastics program for your child</p>
          <div className="kiga-hero-stats">
            <div className="kiga-hero-stat">
              <span className="kiga-stat-number">{classes?.length}+</span>
              <span className="kiga-stat-label">Classes</span>
            </div>
            <div className="kiga-hero-stat">
              <span className="kiga-stat-number">{programs?.length - 1}+</span>
              <span className="kiga-stat-label">Programs</span>
            </div>
            <div className="kiga-hero-stat">
              <span className="kiga-stat-number">4+</span>
              <span className="kiga-stat-label">Expert Coaches</span>
            </div>
          </div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="kiga-classes-filter-section">
        <div className="kiga-classes-container">
          <div className="kiga-filter-header">
            <div className="kiga-search-box">
              <i className="fa-solid fa-search"></i>
              <input
                type="text"
                placeholder="Search classes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button
              className="kiga-filter-toggle"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
            >
              <i className="fa-solid fa-sliders-h"></i>
              Filters
              <span
                className={`kiga-classes-arrow ${isFilterOpen ? "open" : ""}`}
              >
                ▼
              </span>
            </button>
          </div>

          <div className={`kiga-filters-panel ${isFilterOpen ? "open" : ""}`}>
            <div className="kiga-filter-group">
              <label>Program Type</label>
              <div className="kiga-filter-options">
                {programs?.map((program) => (
                  <button
                    key={program}
                    className={`kiga-filter-btn ${
                      selectedProgram === program ? "active" : ""
                    }`}
                    onClick={() => setSelectedProgram(program)}
                  >
                    {program === "all" ? "All Programs" : program}
                  </button>
                ))}
              </div>
            </div>

            <div className="kiga-filter-group">
              <label>Age Group</label>
              <div className="kiga-filter-options">
                {ageGroups?.map((age) => (
                  <button
                    key={age}
                    className={`kiga-filter-btn ${
                      selectedAge === age ? "active" : ""
                    }`}
                    onClick={() => setSelectedAge(age)}
                  >
                    {age === "all" ? "All Ages" : age}
                  </button>
                ))}
              </div>
            </div>

            <div className="kiga-filter-group">
              <label>Day of Week</label>
              <div className="kiga-filter-options">
                {days?.map((day) => (
                  <button
                    key={day}
                    className={`kiga-filter-btn ${
                      selectedDay === day ? "active" : ""
                    }`}
                    onClick={() => setSelectedDay(day)}
                  >
                    {day === "all" ? "All Days" : day}
                  </button>
                ))}
              </div>
            </div>

            {(selectedProgram !== "all" ||
              selectedAge !== "all" ||
              selectedDay !== "all" ||
              searchTerm) && (
              <button className="kiga-clear-filters" onClick={clearFilters}>
                <i className="fa-solid fa-times"></i>
                Clear All Filters
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Results Count */}
      <div className="kiga-results-count">
        <div className="kiga-classes-container">
          <p>
            Showing <strong>{contentData?.allClass?.length}</strong> classes
          </p>
        </div>
      </div>

      {/* Classes Grid */}
      <section className="kiga-classes-grid-section">
        <div className="kiga-class-grid-container">
          {contentData?.allClass?.length === 0 ? (
            <div className="kiga-no-results">
              <i className="fa-solid fa-face-frown"></i>
              <h3>No classes found</h3>
              <p>Try adjusting your filters or search term</p>
              <button onClick={clearFilters} className="kiga-btn-primary">
                Clear Filters
              </button>
            </div>
          ) : (
            <section className="portal-kiga-classes-section">
              {contentData?.allClass?.map((item, key) => (
                <div className="portal-kiga-classes-section-box" key={key}>
                  <Link to={`/booking/classes/details/${key}`}>
                    <div className="portal-kiga-classes-section-box-image">
                      <img src={item?.image} alt="classes" />
                    </div>
                  </Link>
                  <Link to={`/booking/classes/details/${key}`}>
                    <div className="portal-kiga-classes-section-title">
                      <p>{item?.name}</p>
                    </div>
                  </Link>
                  <div className="portal-kiga-classes-section-schedule">
                    <div className="portal-kiga-classes-section-schedule-date">
                      <p>{item?.date}</p>
                    </div>
                    <div className="portal-kiga-schedule-activity">
                      <p>{item?.activity}</p>
                    </div>
                  </div>
                  <div className="portal-kiga-classes-section-days">
                    {item?.days?.map((item, key) => (
                      <p key={key}>{item}</p>
                    ))}
                  </div>
                </div>
              ))}
            </section>
          )}
        </div>
      </section>

      <Footer />
    </div>
  </>
  );
};

export default Classes;
